import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ReportButton from '../components/ReportButton';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI, usersAPI, groupsAPI, uploadAPI } from '../lib/api';
import { useChatWebSocket } from '../hooks/useWebSocket';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Send, ArrowLeft, MessageCircle, Video, Phone, CheckCheck, Check, Users, Plus, Hash, Search, Paperclip, FileText, Image, X, Download } from 'lucide-react';
import MentionTextarea from '../components/MentionTextarea';
import MentionText from '../components/MentionText';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import usePageTitle from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Chat = () => {
  usePageTitle('Chat');
  const { userId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState(null); // { file, preview, uploading }
  const fileInputRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);

  // Group chat state
  const [chatMode, setChatMode] = useState('dm');
  const [myGroups, setMyGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupType, setNewGroupType] = useState('community');
  const [showFindPeople, setShowFindPeople] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingPeople, setSearchingPeople] = useState(false);

  // Get auth token for WebSocket from Firebase
  useEffect(() => {
    const getToken = async () => {
      try {
        const { auth } = await import('../lib/firebase');
        if (auth.currentUser) {
          const idToken = await auth.currentUser.getIdToken();
          setToken(idToken);
        }
      } catch (e) {
        console.error('Failed to get token:', e);
      }
    };
    getToken();
  }, []);

  // WebSocket connection
  const {
    isConnected,
    typingUsers,
    onlineUsers,
    sendChatMessage,
    sendTypingIndicator,
    sendReadReceipt,
    getOnlineStatus
  } = useChatWebSocket(token, (data) => {
    if (data.type === 'chat_message') {
      // New message received
      if (data.message.sender_id === userId || data.message.receiver_id === userId) {
        setMessages(prev => [...prev, data.message]);
        // Send read receipt if chat is open
        if (data.message.sender_id === userId) {
          sendReadReceipt(userId, [data.message.message_id]);
        }
      }
      // Update conversations list
      fetchConversations();
    } else if (data.type === 'message_sent') {
      // Confirm our message was sent
      setMessages(prev => {
        const exists = prev.some(m => m.message_id === data.message.message_id);
        if (exists) return prev;
        return [...prev, data.message];
      });
    } else if (data.type === 'read_receipt') {
      // Update read status
      setMessages(prev => prev.map(m => 
        data.message_ids.includes(m.message_id) ? { ...m, read: true } : m
      ));
    }
  });

  useEffect(() => {
    fetchConversations();
    fetchGroups();
    if (userId) {
      fetchMessages(userId);
      fetchUserProfile(userId);
    }
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, groupMessages]);

  // Get online status for conversation partners
  useEffect(() => {
    if (isConnected && conversations.length > 0) {
      const userIds = conversations.map(c => c.user.user_id);
      getOnlineStatus(userIds);
    }
  }, [isConnected, conversations, getOnlineStatus]);

  const fetchConversations = async () => {
    try {
      const res = await messagesAPI.getConversations();
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const [myRes, allRes] = await Promise.all([
        groupsAPI.myGroups(),
        groupsAPI.list({ limit: 50 })
      ]);
      setMyGroups(myRes.data);
      setAllGroups(allRes.data);
    } catch (e) {
      console.error('Failed to fetch groups:', e);
    }
  };

  const fetchGroupMessages = async (groupId) => {
    try {
      const res = await groupsAPI.getMessages(groupId, { limit: 100 });
      setGroupMessages(res.data);
    } catch (e) {
      console.error('Failed to fetch group messages:', e);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await groupsAPI.create({
        name: newGroupName,
        description: newGroupDesc,
        type: newGroupType
      });
      toast.success('Group created!');
      setShowCreateGroup(false);
      setNewGroupName('');
      setNewGroupDesc('');
      fetchGroups();
      setSelectedGroup(res.data);
      setGroupMessages([]);
    } catch (e) {
      toast.error('Failed to create group');
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await groupsAPI.join(groupId);
      toast.success('Joined group');
      fetchGroups();
    } catch (e) {
      toast.error('Failed to join group');
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await groupsAPI.leave(groupId);
      toast.success('Left group');
      setSelectedGroup(null);
      fetchGroups();
    } catch (e) {
      toast.error('Failed to leave group');
    }
  };

  const handleSearchPeople = async (query) => {
    setPeopleSearch(query);
    if (!query.trim()) { setSearchResults([]); return; }
    setSearchingPeople(true);
    try {
      const res = await usersAPI.listUsers({ search: query, limit: 10 });
      setSearchResults((Array.isArray(res.data) ? res.data : []).filter(u => u.user_id !== user?.user_id));
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearchingPeople(false);
    }
  };

  const startConversation = (targetUserId) => {
    setShowFindPeople(false);
    setPeopleSearch('');
    setSearchResults([]);
    window.location.href = `/chat/${targetUserId}`;
  };

  const handleSendGroupMessage = async () => {
    if (!newMessage.trim() || !selectedGroup) return;
    try {
      const res = await groupsAPI.sendMessage(selectedGroup.group_id, { content: newMessage.trim() });
      setGroupMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (e) {
      toast.error('Failed to send message');
    }
  };

  const fetchMessages = async (uid) => {
    try {
      const res = await messagesAPI.getConversation(uid);
      setMessages(res.data);
      // Mark messages as read
      const unreadIds = res.data
        .filter(m => m.sender_id === uid && !m.read)
        .map(m => m.message_id);
      if (unreadIds.length > 0) {
        sendReadReceipt(uid, unreadIds);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchUserProfile = async (uid) => {
    try {
      const res = await usersAPI.getProfile(uid);
      setSelectedUser(res.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !attachedFile) || !userId) return;

    let fileData = {};

    // Upload file first if attached
    if (attachedFile?.file) {
      setAttachedFile(prev => ({ ...prev, uploading: true }));
      try {
        const uploadRes = await uploadAPI.messageFile(attachedFile.file);
        fileData = {
          file_url: uploadRes.data.url,
          file_name: uploadRes.data.file_name,
          file_type: uploadRes.data.file_type,
        };
      } catch (error) {
        toast.error('Failed to upload file');
        setAttachedFile(prev => ({ ...prev, uploading: false }));
        return;
      }
    }

    const content = newMessage.trim() || (fileData.file_name ? `📎 ${fileData.file_name}` : '');

    // Send via WebSocket for real-time delivery
    const sent = sendChatMessage(userId, content);

    if (!sent) {
      // Fallback to REST API if WebSocket not connected
      try {
        const res = await messagesAPI.send(userId, content, fileData);
        setMessages(prev => [...prev, res.data]);
      } catch (error) {
        console.error('Failed to send message:', error);
        return;
      }
    } else if (fileData.file_url) {
      // If sent via WebSocket, add file data to the local message
      // The WebSocket handler doesn't support file fields yet, so add to last message
      setMessages(prev => {
        const msgs = [...prev];
        if (msgs.length > 0) {
          const last = msgs[msgs.length - 1];
          if (last.sender_id === user?.user_id && last.content === content) {
            msgs[msgs.length - 1] = { ...last, ...fileData };
          }
        }
        return msgs;
      });
    }

    setNewMessage('');
    setAttachedFile(null);
    sendTypingIndicator(userId, false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large. Max 25MB'); return; }
    const isImage = file.type.startsWith('image/');
    setAttachedFile({
      file,
      preview: isImage ? URL.createObjectURL(file) : null,
      name: file.name,
      type: file.type,
      uploading: false,
    });
  };

  const removeAttachment = () => {
    if (attachedFile?.preview) URL.revokeObjectURL(attachedFile.preview);
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Accepts either a DOM event (legacy Input) or a raw string (MentionTextarea).
  const handleTyping = (eOrValue) => {
    const v = typeof eOrValue === 'string' ? eOrValue : eOrValue.target.value;
    setNewMessage(v);

    // Send typing indicator
    if (!isTyping && userId) {
      setIsTyping(true);
      sendTypingIndicator(userId, true);
    }

    // Clear typing indicator after 2 seconds of no typing
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (userId) sendTypingIndicator(userId, false);
    }, 2000);
  };

  const startVideoCall = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/video/create-room`, {
        provider_id: null
      }, { withCredentials: true });
      window.open(`/video/${res.data.room_id}`, '_blank');
    } catch (error) {
      console.error('Failed to create video room:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto h-[calc(100vh-8rem)] md:h-[calc(100vh-5rem)]">
        <div className="flex h-full bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-ambient mx-4 my-4">
          {/* Conversations List */}
          <div className={`w-full md:w-80 border-r border-slate-100 flex flex-col ${userId || selectedGroup ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h2 className="font-heading font-bold text-xl text-dark-slate">Messages</h2>
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setShowFindPeople(true)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {isConnected && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></div>
                    Live
                  </Badge>
                )}
              </div>
              <div className="flex bg-slate-100 rounded-lg p-0.5">
                <button
                  onClick={() => setChatMode('dm')}
                  className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-colors ${chatMode === 'dm' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'}`}
                  data-testid="chat-mode-dm"
                >
                  Direct
                </button>
                <button
                  onClick={() => { setChatMode('groups'); fetchGroups(); }}
                  className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-colors ${chatMode === 'groups' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'}`}
                  data-testid="chat-mode-groups"
                >
                  Groups
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {chatMode === 'dm' ? (
                <>
                  {conversations.length === 0 ? (
                    <div className="p-6 text-center">
                      <MessageCircle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                      <p className="text-slate-gray mb-3">No conversations yet</p>
                      <p className="text-xs text-slate-gray mb-4">Find alumni to connect with</p>
                      <Button variant="outline" className="rounded-full text-sm" onClick={() => setShowFindPeople(true)}>
                        <Search className="w-4 h-4 mr-2" />Find People
                      </Button>
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <Link
                        key={conv.user.user_id}
                        to={`/chat/${conv.user.user_id}`}
                        className={`flex items-center p-4 hover:bg-slate-50 transition-colors ${
                          userId === conv.user.user_id ? 'bg-mint-fresh/20' : ''
                        }`}
                        data-testid={`conv-${conv.user.user_id}`}
                      >
                        <div className="relative">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={conv.user.profile_photo} />
                            <AvatarFallback className="bg-mint-fresh text-dark-slate">
                              {conv.user.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          {onlineUsers[conv.user.user_id] && (
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        <div className="flex-1 ml-3 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-dark-slate truncate">{conv.user.name}</p>
                            <span className="text-xs text-slate-gray">
                              {formatDistanceToNow(new Date(conv.last_time), { addSuffix: true })}
                            </span>
                          </div>
                          <div className="flex items-center">
                            {typingUsers[conv.user.user_id] ? (
                              <span className="text-sm text-emerald-600 italic">typing...</span>
                            ) : (
                              <p className="text-sm text-slate-gray truncate">{conv.last_message}</p>
                            )}
                          </div>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="ml-2 w-5 h-5 bg-dark-slate text-white text-xs rounded-full flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </Link>
                    ))
                  )}
                </>
              ) : (
                <>
                  <div className="p-3">
                    <Button
                      onClick={() => setShowCreateGroup(true)}
                      variant="outline"
                      className="w-full rounded-xl text-sm"
                      data-testid="create-group-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Create Group
                    </Button>
                  </div>
                  {myGroups.length > 0 && (
                    <div className="px-3 pb-1">
                      <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide">My Groups</p>
                    </div>
                  )}
                  {myGroups.map(g => (
                    <button
                      key={g.group_id}
                      onClick={() => { setSelectedGroup(g); fetchGroupMessages(g.group_id); }}
                      className={`w-full flex items-center p-4 hover:bg-slate-50 transition-colors text-left ${
                        selectedGroup?.group_id === g.group_id ? 'bg-mint-fresh/20' : ''
                      }`}
                      data-testid={`group-${g.group_id}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-mint-fresh to-emerald-200 flex items-center justify-center">
                        <Hash className="w-5 h-5 text-dark-slate" />
                      </div>
                      <div className="flex-1 ml-3 min-w-0">
                        <p className="font-semibold text-dark-slate truncate">{g.name}</p>
                        <p className="text-xs text-slate-gray">{g.member_count} members</p>
                      </div>
                    </button>
                  ))}
                  {allGroups.filter(g => !myGroups.some(m => m.group_id === g.group_id)).length > 0 && (
                    <>
                      <div className="px-3 pt-3 pb-1">
                        <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide">Discover</p>
                      </div>
                      {allGroups.filter(g => !myGroups.some(m => m.group_id === g.group_id)).map(g => (
                        <div key={g.group_id} className="flex items-center p-4 hover:bg-slate-50">
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                            <Hash className="w-5 h-5 text-slate-gray" />
                          </div>
                          <div className="flex-1 ml-3 min-w-0">
                            <p className="font-semibold text-dark-slate truncate">{g.name}</p>
                            <p className="text-xs text-slate-gray">{g.member_count} members</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-lg text-xs"
                            onClick={() => handleJoinGroup(g.group_id)}
                            data-testid={`join-group-${g.group_id}`}
                          >
                            Join
                          </Button>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Chat Area */}
          {userId ? (
            <div className="flex-1 flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center">
                  <Link to="/chat" className="md:hidden mr-3" onClick={() => { setSelectedGroup(null); }}>
                    <ArrowLeft className="w-6 h-6 text-dark-slate" />
                  </Link>
                  {selectedUser && (
                    <Link
                      to={`/profile/${selectedUser.user_id || userId}`}
                      className="flex items-center group"
                      aria-label={`View ${selectedUser.name}'s profile`}
                    >
                      <div className="relative">
                        <Avatar className="w-10 h-10 group-hover:ring-2 group-hover:ring-mint-fresh transition-all">
                          <AvatarImage src={selectedUser.profile_photo} />
                          <AvatarFallback className="bg-mint-fresh text-dark-slate">
                            {selectedUser.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        {onlineUsers[userId] && (
                          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="font-semibold text-dark-slate group-hover:underline">{selectedUser.name}</p>
                        <p className="text-xs text-slate-gray">
                          {typingUsers[userId] ? (
                            <span className="text-emerald-600">typing...</span>
                          ) : onlineUsers[userId] ? (
                            <span className="text-emerald-600">Online</span>
                          ) : (
                            selectedUser.institution || 'Offline'
                          )}
                        </p>
                      </div>
                    </Link>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="rounded-full"
                    onClick={startVideoCall}
                    data-testid="start-video-call"
                  >
                    <Video className="w-5 h-5 text-slate-gray" />
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Phone className="w-5 h-5 text-slate-gray" />
                  </Button>
                  {selectedUser && selectedUser.user_id && (
                    <div className="px-2">
                      <ReportButton contentType="user" contentId={selectedUser.user_id} />
                    </div>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        msg.sender_id === user?.user_id
                          ? 'bg-dark-slate text-white rounded-br-md'
                          : 'bg-slate-100 text-dark-slate rounded-bl-md'
                      }`}
                    >
                      {/* File attachment */}
                      {msg.file_url && (
                        msg.file_type === 'image' ? (
                          <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="block mb-1">
                            <img src={msg.file_url} alt={msg.file_name || 'Image'} className="max-w-full max-h-48 rounded-lg object-cover" />
                          </a>
                        ) : (
                          <a href={msg.file_url} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${msg.sender_id === user?.user_id ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-200 hover:bg-slate-300'} transition-colors`}>
                            <FileText className="w-5 h-5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{msg.file_name || 'File'}</p>
                              <p className="text-[10px] opacity-70">{msg.file_type === 'document' ? 'Document' : msg.file_type === 'video' ? 'Video' : 'File'}</p>
                            </div>
                            <Download className="w-4 h-4 flex-shrink-0 opacity-70" />
                          </a>
                        )
                      )}
                      {msg.content && !msg.content.startsWith('📎 ') && <p><MentionText text={msg.content} /></p>}
                      {msg.content && msg.content.startsWith('📎 ') && !msg.file_url && <p>{msg.content}</p>}
                      <div className={`flex items-center justify-end space-x-1 mt-1 ${
                        msg.sender_id === user?.user_id ? 'text-slate-300' : 'text-slate-400'
                      }`}>
                        <span className="text-xs">
                          {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                        </span>
                        {msg.sender_id === user?.user_id && (
                          msg.read ? (
                            <CheckCheck className="w-3.5 h-3.5 text-blue-400" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Typing indicator */}
                {typingUsers[userId] && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-slate-100">
                {/* Attachment preview */}
                {attachedFile && (
                  <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-xl">
                    {attachedFile.preview ? (
                      <img src={attachedFile.preview} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-dark-slate truncate">{attachedFile.name}</p>
                      <p className="text-xs text-slate-gray">{attachedFile.uploading ? 'Uploading...' : 'Ready to send'}</p>
                    </div>
                    <button onClick={removeAttachment} className="p-1 rounded-full hover:bg-slate-200">
                      <X className="w-4 h-4 text-slate-gray" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,video/*" />
                  <Button
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-10 h-10 rounded-full p-0 text-slate-gray hover:text-dark-slate hover:bg-slate-100"
                    title="Attach file"
                  >
                    <Paperclip className="w-5 h-5" />
                  </Button>
                  <MentionTextarea
                    placeholder="Type a message... type @ to mention, Shift+Enter for new line"
                    value={newMessage}
                    onChange={handleTyping}
                    onKeyDown={handleKeyPress}
                    rows={3}
                    className="flex-1 rounded-2xl bg-slate-50 border-slate-200 min-h-[88px] max-h-[260px] py-3 px-4"
                    data-testid="message-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!newMessage.trim() && !attachedFile}
                    className="w-12 h-12 rounded-full bg-dark-slate text-white"
                    data-testid="send-message"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          ) : selectedGroup ? (
            <div className="flex-1 flex flex-col">
              {/* Group Chat Header */}
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center">
                  <button onClick={() => setSelectedGroup(null)} className="md:hidden mr-3">
                    <ArrowLeft className="w-6 h-6 text-dark-slate" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-mint-fresh to-emerald-200 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-dark-slate" />
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-dark-slate">{selectedGroup.name}</p>
                    <p className="text-xs text-slate-gray">{selectedGroup.member_count} members</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg text-xs text-red-600"
                  onClick={() => handleLeaveGroup(selectedGroup.group_id)}
                  data-testid="leave-group-btn"
                >
                  Leave
                </Button>
              </div>

              {/* Group Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {groupMessages.map(msg => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${msg.sender_id !== user?.user_id ? 'flex items-start gap-2' : ''}`}>
                      {msg.sender_id !== user?.user_id && (
                        <Avatar className="w-7 h-7 flex-shrink-0 mt-1">
                          <AvatarImage src={msg.sender_photo} />
                          <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{msg.sender_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                      )}
                      <div className={`rounded-2xl px-4 py-2 ${
                        msg.sender_id === user?.user_id
                          ? 'bg-dark-slate text-white rounded-br-md'
                          : 'bg-slate-100 text-dark-slate rounded-bl-md'
                      }`}>
                        {msg.sender_id !== user?.user_id && (
                          <p className="text-xs font-medium text-emerald-600 mb-0.5">{msg.sender_name}</p>
                        )}
                        <p><MentionText text={msg.content} /></p>
                        <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? 'text-slate-300' : 'text-slate-400'}`}>
                          {msg.created_at ? formatDistanceToNow(new Date(msg.created_at), { addSuffix: true }) : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Group Input */}
              <div className="p-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Message group..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendGroupMessage(); }
                    }}
                    className="flex-1 h-12 rounded-full bg-slate-50 border-slate-200"
                    data-testid="group-message-input"
                  />
                  <Button
                    onClick={handleSendGroupMessage}
                    disabled={!newMessage.trim()}
                    className="w-12 h-12 rounded-full bg-dark-slate text-white"
                    data-testid="send-group-message"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="hidden md:flex flex-1 items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">
                  Select a conversation
                </h3>
                <p className="text-slate-gray">Choose a conversation or group to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              data-testid="group-name-input"
            />
            <Textarea
              placeholder="Description (optional)"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
              rows={2}
              data-testid="group-desc-input"
            />
            <div className="flex gap-2">
              {['community', 'institution', 'interest'].map(t => (
                <Button
                  key={t}
                  variant={newGroupType === t ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-lg capitalize"
                  onClick={() => setNewGroupType(t)}
                >
                  {t}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateGroup(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()} className="bg-dark-slate text-white" data-testid="confirm-create-group">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Find People Dialog */}
      <Dialog open={showFindPeople} onOpenChange={setShowFindPeople}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Conversation</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
            <Input
              placeholder="Search by name..."
              value={peopleSearch}
              onChange={(e) => handleSearchPeople(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto mt-2">
            {searchingPeople ? (
              <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-dark-slate"></div></div>
            ) : searchResults.length > 0 ? (
              searchResults.map(person => (
                <button
                  key={person.user_id}
                  onClick={() => startConversation(person.user_id)}
                  className="w-full flex items-center p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={person.profile_photo} />
                    <AvatarFallback className="bg-mint-fresh text-dark-slate">{person.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="ml-3">
                    <p className="font-medium text-dark-slate">{person.name}</p>
                    <p className="text-xs text-slate-gray">{person.institution || ''}{person.title ? ` · ${person.title}` : ''}</p>
                  </div>
                </button>
              ))
            ) : peopleSearch.trim() ? (
              <p className="text-center text-slate-gray text-sm py-6">No alumni found</p>
            ) : (
              <p className="text-center text-slate-gray text-sm py-6">Search for alumni to start a conversation</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Chat;
